/**
 * 矩形类元素手柄：八向 resize + 可选圆角柄 + 顶部旋转柄。
 * 用于 rect、image、text、circle、triangle 等。
 *
 * 所有手柄采用「透明热区 + 视觉小方块」双层结构，
 * 确保鼠标悬停时光标切换稳定可靠。
 */
import type { ResizeHandle } from '@/components/canvas/transform';
import { handleChromeWorldMetrics, selectionChromeWorldMetrics } from '../selectionChrome';
import { SelectionHandleVisual, SelectionRotationStem } from '../SelectionHandleVisual';
import type { HandlesProps } from './types';

const resizeCursorMap: Record<ResizeHandle, string> = {
  nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize',
  se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize',
};

const CORNER_HANDLES: ResizeHandle[] = ['nw', 'ne', 'se', 'sw'];
const EDGE_HANDLES: ResizeHandle[] = ['n', 'e', 's', 'w'];

const radiusCursorMap: Record<'tl' | 'tr' | 'br' | 'bl', string> = {
  tl: 'nwse-resize', tr: 'nesw-resize', br: 'nwse-resize', bl: 'nesw-resize',
};

export type RectHandlesProps = HandlesProps;

export function RectHandles({
  element: el,
  rect,
  zoom,
  currentRadius,
  isRadiusDragging = false,
  resizeHandles: resizeHandlesFilter,
  showRotate = true,
  showRadiusHandles = true,
  onStartResize,
  onStartRotate,
  onStartRadiusDrag,
}: RectHandlesProps) {
  const { inset, pad } = selectionChromeWorldMetrics(zoom);
  const w = rect.width + pad;
  const h = rect.height + pad;
  const {
    hitSize,
    hitHalf,
    rotationLineHeight,
    rotationHandleCenterY,
    radiusHitSize,
    radiusRest,
    radiusCenterDotPx,
  } = handleChromeWorldMetrics(zoom);

  const allowedCorners = (() => {
    if (resizeHandlesFilter && resizeHandlesFilter.length > 0) {
      return CORNER_HANDLES.filter((h) => resizeHandlesFilter.includes(h));
    }
    return CORNER_HANDLES;
  })();

  const allowedEdges = (() => {
    if (resizeHandlesFilter && resizeHandlesFilter.length > 0) {
      const onlyCorners =
        resizeHandlesFilter.length > 0 &&
        resizeHandlesFilter.every((h) => CORNER_HANDLES.includes(h));
      if (onlyCorners) return EDGE_HANDLES;
      return EDGE_HANDLES.filter((h) => resizeHandlesFilter.includes(h));
    }
    return EDGE_HANDLES;
  })();

  const cornerHandleCenters = (
    [
      { handle: 'nw' as ResizeHandle, cx: 0, cy: 0 },
      { handle: 'ne' as ResizeHandle, cx: w, cy: 0 },
      { handle: 'se' as ResizeHandle, cx: w, cy: h },
      { handle: 'sw' as ResizeHandle, cx: 0, cy: h },
    ] satisfies { handle: ResizeHandle; cx: number; cy: number }[]
  ).filter(({ handle }) => allowedCorners.includes(handle));

  const hasBorderRadius = showRadiusHandles && ['rect', 'image', 'text'].includes(el.type);
  const radiusHandlePositions: { corner: 'tl' | 'tr' | 'br' | 'bl'; left: number; top: number }[] = (() => {
    const rHitHalf = radiusHitSize / 2;
    const r = currentRadius;
    const effectiveR = isRadiusDragging ? Math.max(1, r) : Math.max(radiusRest, r);
    return [
      { corner: 'tl', left: inset + effectiveR - rHitHalf, top: inset + effectiveR - rHitHalf },
      { corner: 'tr', left: w - inset - effectiveR - rHitHalf, top: inset + effectiveR - rHitHalf },
      { corner: 'br', left: w - inset - effectiveR - rHitHalf, top: h - inset - effectiveR - rHitHalf },
      { corner: 'bl', left: inset + effectiveR - rHitHalf, top: h - inset - effectiveR - rHitHalf },
    ];
  })();

  return (
    <>
      {showRotate && (
        <div
          className="absolute pointer-events-none flex justify-center"
          style={{
            left: w / 2,
            top: -rotationLineHeight,
            width: 0,
            height: rotationLineHeight,
            transform: 'translateX(-50%)',
          }}
        >
          <SelectionRotationStem zoom={zoom} lineHeightWorld={rotationLineHeight} />
        </div>
      )}

      {/* 四边：整条边线可拖拽缩放（无中间可视方块） */}
      {allowedEdges.includes('n') && (
        <div
          data-handle
          data-handle-cursor="ns-resize"
          className="absolute pointer-events-auto"
          style={{
            left: hitHalf,
            top: -hitHalf,
            width: Math.max(hitSize, w - hitSize * 2),
            height: hitSize,
            cursor: 'ns-resize',
            zIndex: 2,
          }}
          onMouseDown={(e) => onStartResize('n', e)}
        />
      )}
      {allowedEdges.includes('s') && (
        <div
          data-handle
          data-handle-cursor="ns-resize"
          className="absolute pointer-events-auto"
          style={{
            left: hitHalf,
            top: h - hitHalf,
            width: Math.max(hitSize, w - hitSize * 2),
            height: hitSize,
            cursor: 'ns-resize',
            zIndex: 2,
          }}
          onMouseDown={(e) => onStartResize('s', e)}
        />
      )}
      {allowedEdges.includes('w') && (
        <div
          data-handle
          data-handle-cursor="ew-resize"
          className="absolute pointer-events-auto"
          style={{
            left: -hitHalf,
            top: hitHalf,
            width: hitSize,
            height: Math.max(hitSize, h - hitSize * 2),
            cursor: 'ew-resize',
            zIndex: 2,
          }}
          onMouseDown={(e) => onStartResize('w', e)}
        />
      )}
      {allowedEdges.includes('e') && (
        <div
          data-handle
          data-handle-cursor="ew-resize"
          className="absolute pointer-events-auto"
          style={{
            left: w - hitHalf,
            top: hitHalf,
            width: hitSize,
            height: Math.max(hitSize, h - hitSize * 2),
            cursor: 'ew-resize',
            zIndex: 2,
          }}
          onMouseDown={(e) => onStartResize('e', e)}
        />
      )}

      {/* 四角可视 resize 手柄 */}
      {cornerHandleCenters.map(({ handle, cx, cy }) => (
        <div
          key={handle}
          data-handle
          data-handle-cursor={resizeCursorMap[handle]}
          className="absolute pointer-events-auto flex items-center justify-center"
          style={{
            left: cx - hitHalf,
            top: cy - hitHalf,
            width: hitSize,
            height: hitSize,
            zIndex: 3,
            cursor: resizeCursorMap[handle],
          }}
          onMouseDown={(e) => onStartResize(handle, e)}
        >
          <SelectionHandleVisual zoom={zoom} className="pointer-events-none shrink-0" />
        </div>
      ))}

      {/* 圆角手柄 */}
      {hasBorderRadius && radiusHandlePositions.map(({ corner, left, top }) => (
        <div
          key={corner}
          data-handle
          data-radius-handle={corner}
          data-handle-cursor={radiusCursorMap[corner]}
          className="absolute rounded-full pointer-events-auto flex items-center justify-center"
          style={{ left, top, width: radiusHitSize, height: radiusHitSize, zIndex: 2, cursor: radiusCursorMap[corner] }}
          onMouseDown={(e) => onStartRadiusDrag(corner, e)}
        >
          <SelectionHandleVisual zoom={zoom} className="rounded-full pointer-events-none shrink-0" />
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              left: (radiusHitSize - radiusCenterDotPx) / 2,
              top: (radiusHitSize - radiusCenterDotPx) / 2,
              width: radiusCenterDotPx,
              height: radiusCenterDotPx,
              backgroundColor: 'var(--accent)',
            }}
          />
        </div>
      ))}

      {showRotate && (
        <div
          data-handle
          data-handle-cursor="grab"
          className="absolute pointer-events-auto flex items-center justify-center cursor-grab"
          style={{
            left: w / 2 - hitHalf,
            top: rotationHandleCenterY - hitHalf,
            width: hitSize,
            height: hitSize,
            zIndex: 4,
          }}
          onMouseDown={onStartRotate}
        >
          <SelectionHandleVisual zoom={zoom} className="rounded-full pointer-events-none shrink-0" />
        </div>
      )}
    </>
  );
}
